import type { FortuneResult } from './fortune-engine';
import { saveFortuneImage, type FortuneSaveMode } from './fortune-save';
import { loadKakaoSdk, type KakaoSdk, type KakaoShareOptions } from './kakao-share';

export type ShareMode = 'kakao' | 'kakao-ready';

interface PreparedKakaoShare {
  readonly kakao: KakaoSdk;
  readonly options: KakaoShareOptions;
}

const FORTUNE_URL = 'https://oneultool.com/tools/today-fortune/';
const preparedKakaoShares = new Map<string, PreparedKakaoShare>();

type MeasureText = (text: string) => number;

export function ellipsizeText(text: string, maxWidth: number, measureText: MeasureText): string {
  const normalized = text.trim();
  if (!normalized || maxWidth <= 0) return '';
  if (measureText(normalized) <= maxWidth) return normalized;

  const ellipsis = '…';
  if (measureText(ellipsis) > maxWidth) return '';
  const characters = Array.from(normalized);
  while (characters.length > 0 && measureText(`${characters.join('').trimEnd()}${ellipsis}`) > maxWidth) {
    characters.pop();
  }
  return `${characters.join('').trimEnd()}${ellipsis}`;
}

export function wrapText(
  text: string,
  maxWidth: number,
  maxLines: number,
  measureText: MeasureText,
): string[] {
  const normalized = text.trim().replace(/\s+/g, ' ');
  if (!normalized || maxWidth <= 0 || maxLines <= 0) return [];
  if (measureText(normalized) <= maxWidth) return [normalized];

  const characters = Array.from(normalized);
  const lines: string[] = [];
  let line = '';

  for (let index = 0; index < characters.length; index += 1) {
    const character = characters[index]!;
    const candidate = `${line}${character}`;
    if (!line || measureText(candidate) <= maxWidth) {
      line = candidate;
      continue;
    }

    if (lines.length === maxLines - 1) {
      const remainder = `${line}${characters.slice(index).join('')}`;
      lines.push(ellipsizeText(remainder, maxWidth, measureText));
      return lines;
    }

    lines.push(line.trimEnd());
    line = character.trimStart();
  }

  if (line && lines.length < maxLines) lines.push(line.trimEnd());
  return lines;
}

export function needsPreparedKakaoTap(userAgent: string, maxTouchPoints = 0): boolean {
  return /Android|iPhone|iPad|iPod/i.test(userAgent)
    || (/Macintosh/i.test(userAgent) && maxTouchPoints > 1);
}

function roundedRect(
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
): void {
  const corner = Math.min(radius, width / 2, height / 2);
  context.beginPath();
  context.moveTo(x + corner, y);
  context.lineTo(x + width - corner, y);
  context.quadraticCurveTo(x + width, y, x + width, y + corner);
  context.lineTo(x + width, y + height - corner);
  context.quadraticCurveTo(x + width, y + height, x + width - corner, y + height);
  context.lineTo(x + corner, y + height);
  context.quadraticCurveTo(x, y + height, x, y + height - corner);
  context.lineTo(x, y + corner);
  context.quadraticCurveTo(x, y, x + corner, y);
  context.closePath();
}

function drawBannerCookie(context: CanvasRenderingContext2D): void {
  context.save();
  context.translate(421, 160);
  context.scale(1.7, 1.7);

  context.fillStyle = 'rgba(0, 0, 0, .08)';
  context.beginPath();
  context.ellipse(70, 86, 46, 8, 0, 0, Math.PI * 2);
  context.fill();

  const cookieGradient = context.createLinearGradient(0, 6, 0, 69);
  cookieGradient.addColorStop(0, '#ffffff');
  cookieGradient.addColorStop(1, '#ffe7a8');
  context.fillStyle = cookieGradient;
  context.strokeStyle = '#8a5a12';
  context.lineWidth = 1.4;
  context.beginPath();
  context.moveTo(12, 55);
  context.bezierCurveTo(12, 22, 46, 6, 70, 6);
  context.bezierCurveTo(94, 6, 128, 22, 128, 55);
  context.bezierCurveTo(128, 64, 118, 69, 108, 62);
  context.bezierCurveTo(96, 54, 82, 48, 70, 48);
  context.bezierCurveTo(58, 48, 44, 54, 32, 62);
  context.bezierCurveTo(22, 69, 12, 64, 12, 55);
  context.closePath();
  context.fill();
  context.stroke();

  context.strokeStyle = 'rgba(138, 90, 18, .5)';
  context.lineWidth = 2.4;
  context.lineCap = 'round';
  context.beginPath();
  context.moveTo(34, 50);
  context.quadraticCurveTo(70, 58, 106, 50);
  context.stroke();
  context.restore();
}

function drawScorePill(context: CanvasRenderingContext2D, score: number): void {
  const label = '오늘의 운세 점수';
  const scoreText = String(score);
  const suffix = '/100';
  const gap = 10;
  const horizontalPadding = 44;

  context.font = '600 22px "Noto Sans KR", sans-serif';
  const labelWidth = context.measureText(label).width;
  context.font = '800 88px "Lexend Deca", sans-serif';
  const scoreWidth = context.measureText(scoreText).width;
  context.font = '600 26px "Lexend Deca", sans-serif';
  const suffixWidth = context.measureText(suffix).width;

  const width = horizontalPadding * 2 + labelWidth + scoreWidth + suffixWidth + gap * 2;
  const x = (1080 - width) / 2;
  const y = 410;
  const height = 128;

  context.save();
  context.shadowColor = 'rgba(0, 0, 0, .12)';
  context.shadowBlur = 34;
  context.shadowOffsetY = 16;
  roundedRect(context, x, y, width, height, 40);
  context.fillStyle = '#ffffff';
  context.fill();
  context.restore();

  const baseline = 504;
  let cursor = x + horizontalPadding;
  context.textAlign = 'left';
  context.textBaseline = 'alphabetic';
  context.fillStyle = '#6e6a7c';
  context.font = '600 22px "Noto Sans KR", sans-serif';
  context.fillText(label, cursor, baseline);
  cursor += labelWidth + gap;
  context.fillStyle = '#5f33e1';
  context.font = '800 88px "Lexend Deca", sans-serif';
  context.fillText(scoreText, cursor, baseline);
  cursor += scoreWidth + gap;
  context.fillStyle = '#6e6a7c';
  context.font = '600 26px "Lexend Deca", sans-serif';
  context.fillText(suffix, cursor, baseline);
}

async function fortuneCanvas(result: FortuneResult): Promise<HTMLCanvasElement> {
  await document.fonts?.ready;
  const canvas = document.createElement('canvas');
  canvas.width = 1080;
  canvas.height = 1080;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('운세 카드를 그릴 수 없어요.');

  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, 1080, 1080);
  const bannerGradient = context.createLinearGradient(0, 0, 1080, 500);
  bannerGradient.addColorStop(0, '#ffe7a8');
  bannerGradient.addColorStop(1, '#e3a73e');
  context.fillStyle = bannerGradient;
  context.fillRect(0, 0, 1080, 500);

  context.textBaseline = 'alphabetic';
  context.textAlign = 'left';
  context.fillStyle = '#24252c';
  context.font = '700 34px "Noto Sans KR", sans-serif';
  context.fillText('오늘툴', 64, 105);
  const brandWidth = context.measureText('오늘툴').width;
  context.fillStyle = '#5f33e1';
  context.fillText('.', 64 + brandWidth, 105);

  const dateText = `${result.dateLabel}의 한입 운세`;
  context.font = '500 22px "Noto Sans KR", sans-serif';
  context.fillStyle = '#5c4415';
  context.textAlign = 'right';
  context.fillText(ellipsizeText(dateText, 440, (text) => context.measureText(text).width), 1016, 103);

  drawBannerCookie(context);
  drawScorePill(context, result.overall);

  const contentX = 64;
  const contentWidth = 952;
  const headlineY = 620;
  const headlineLineHeight = 62;
  context.textAlign = 'left';
  context.textBaseline = 'top';
  context.fillStyle = '#24252c';
  context.font = '700 48px "Noto Sans KR", sans-serif';
  const headlineLines = wrapText(
    result.headline,
    contentWidth,
    2,
    (text) => context.measureText(text).width,
  );
  headlineLines.forEach((line, index) => {
    context.fillText(line, contentX, headlineY + index * headlineLineHeight);
  });

  const headlineBottom = headlineY + Math.max(1, headlineLines.length) * headlineLineHeight;
  const dividerY = headlineBottom + 30;
  roundedRect(context, contentX, dividerY, 72, 3, 2);
  context.fillStyle = '#e3a73e';
  context.fill();

  const detailsY = dividerY + 37;
  const columnGap = 40;
  const availableColumnWidth = contentWidth - columnGap * 2;
  const unit = availableColumnWidth / 3.6;
  const columns = [
    { x: contentX, width: unit * 1.6, label: '오늘 해볼 일', value: result.todo },
    { x: contentX + unit * 1.6 + columnGap, width: unit, label: '행운의 색', value: result.luckyColor.name },
    { x: contentX + unit * 2.6 + columnGap * 2, width: unit, label: '행운의 숫자', value: String(result.luckyNumber) },
  ] as const;

  for (const column of columns) {
    context.textBaseline = 'top';
    context.fillStyle = '#6e6a7c';
    context.font = '500 18px "Noto Sans KR", sans-serif';
    context.fillText(column.label, column.x, detailsY);
    context.fillStyle = '#24252c';
    context.font = '700 25px "Noto Sans KR", sans-serif';
    const value = ellipsizeText(column.value, column.width, (text) => context.measureText(text).width);
    context.fillText(value, column.x, detailsY + 34);
  }

  context.fillStyle = '#6e6a7c';
  context.font = '500 20px "Noto Sans KR", "Lexend Deca", sans-serif';
  context.fillText('오늘의 작은 재미 · oneultool.com', contentX, 1001);
  return canvas;
}

function canvasBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error('운세 이미지 파일을 만들지 못했어요.'));
    }, 'image/png');
  });
}

export async function createFortuneCardFile(result: FortuneResult): Promise<File> {
  const canvas = await fortuneCanvas(result);
  const blob = await canvasBlob(canvas);
  return new File([blob], `오늘툴-한입운세-${result.dateKey}.png`, { type: 'image/png' });
}

export async function downloadFortuneCard(result: FortuneResult, returnFocusTo?: HTMLElement): Promise<FortuneSaveMode> {
  return saveFortuneImage(await createFortuneCardFile(result), returnFocusTo);
}

function shareCacheKey(result: FortuneResult): string {
  return [result.dateKey, result.overall, result.headline, result.todo, result.luckyColor.name, result.luckyNumber].join('|');
}

function sendPreparedKakaoShare(prepared: PreparedKakaoShare): void {
  prepared.kakao.Share.sendDefault(prepared.options);
}

async function prepareKakaoShare(file: File, result: FortuneResult, key: string): Promise<PreparedKakaoShare> {
  const kakao = await loadKakaoSdk(key);
  const response = await kakao.Share.uploadImage({ file: [file] });
  const imageUrl = response.infos.original.url;
  return {
    kakao,
    options: {
      objectType: 'feed',
      content: {
        title: `${result.dateLabel}, ${result.headline}`,
        description: `오늘의 운세 점수 ${result.overall}점 · 행운의 숫자 ${result.luckyNumber}`,
        imageUrl,
        link: { mobileWebUrl: FORTUNE_URL, webUrl: FORTUNE_URL },
      },
      buttons: [{
        title: '나도 오늘 운세 보기',
        link: { mobileWebUrl: FORTUNE_URL, webUrl: FORTUNE_URL },
      }],
    },
  };
}

export async function shareFortuneCard(result: FortuneResult, kakaoKey: string): Promise<ShareMode> {
  if (!kakaoKey) throw new Error('카카오톡 카드 공유를 준비하지 못했어요. 이미지로 저장을 이용해 주세요.');
  const cacheKey = `${kakaoKey}:${shareCacheKey(result)}`;
  const prepared = preparedKakaoShares.get(cacheKey);
  if (prepared) {
    // Keep this before the first await: mobile browsers require a direct tap to open KakaoTalk.
    sendPreparedKakaoShare(prepared);
    return 'kakao';
  }

  const file = await createFortuneCardFile(result);
  let nextShare: PreparedKakaoShare;
  try {
    nextShare = await prepareKakaoShare(file, result, kakaoKey);
    preparedKakaoShares.set(cacheKey, nextShare);
  } catch {
    throw new Error('카카오톡 공유 카드를 준비하지 못했어요. 잠시 후 다시 시도해 주세요.');
  }

  if (needsPreparedKakaoTap(navigator.userAgent, navigator.maxTouchPoints)) {
    return 'kakao-ready';
  }

  try {
    sendPreparedKakaoShare(nextShare);
    return 'kakao';
  } catch {
    return 'kakao-ready';
  }
}
